import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Img,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import moment from "moment-timezone";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactPaginate from "react-paginate";
import { AiOutlineMore } from "react-icons/ai";
import useDebounce from "../../../hooks/useDebounce";
import SearchableSelect from "../../../components/selectForm";
import DeleteDialog from "../../../components/dialog/delete";
import { lateCheckinCall } from "../../../api/lateCheckinRequest";
import calendarIcon from "../../../assets/images/onleave/calendar.svg";
import chevronLeft from "../../../assets/images/employee/chevron-left.svg";
import chevronRight from "../../../assets/images/employee/chevron-right.svg";
import iconApproved from "../../../assets/images/receiveSalary/check-circle.svg";
import iconRejected from "../../../assets/images/contract/x-circle.svg";
import iconEdit from "../../../assets/images/employee/edit.svg";
import iconDelete from "../../../assets/images/timeKeeping/delete.svg";
import LateCheckinForm from "./LateCheckinForm";

/**
 * Quản lý chấm công -> tab "Đơn xin đi muộn" (05/11/2026) - thay "Thêm nhân viên" theo điểm chấm công.
 * NV gửi đơn trên app, quản lý duyệt bước 1 trên app, HCNS duyệt bước 2 ở đây (đơn mới có hiệu lực).
 * HCNS thêm / sửa / xoá NV được đi muộn trực tiếp; xuất Excel theo bộ lọc (nút ở tiêu đề trang).
 * Giao diện theo tab "Đơn chỉnh sửa công".
 */

const TZ = "Asia/Ho_Chi_Minh";
const baseURL = process.env.REACT_APP_BACKEND_API || "http://localhost:3001";
const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { key: "PENDING", value: "Chờ duyệt" },
  { key: "APPROVED", value: "Đã duyệt" },
  { key: "REJECTED", value: "Từ chối" },
];
const LEADER_STATUS_OPTIONS = [
  { key: "PENDING", value: "Chưa duyệt" },
  { key: "APPROVED", value: "Đã duyệt" },
  { key: "REJECTED", value: "Từ chối" },
];
const VALIDITY_OPTIONS = [
  { key: "ACTIVE", value: "Đang hiệu lực" },
  { key: "UPCOMING", value: "Chưa đến ngày" },
  { key: "EXPIRED", value: "Hết hiệu lực" },
];
const SOURCE_TEXT = {
  APP: "Nhân viên gửi đơn",
  IMPORT: "Nhập từ Excel",
  WEB: "HCNS thêm trên web",
};

const HEADERS = [
  { label: "", key: "action" },
  { label: "HCNS duyệt (bước 2)", key: "status" },
  { label: "Quản lý duyệt (bước 1)", key: "leader" },
  { label: "Mã nhân viên", key: "code" },
  { label: "Tên nhân viên", key: "fullName" },
  { label: "Số phút được đi muộn", key: "extraTime" },
  { label: "Thời gian hiệu lực", key: "range" },
  { label: "Hiệu lực", key: "validity" },
  { label: "Lý do", key: "reason" },
  { label: "Ảnh", key: "images" },
  { label: "Nguồn", key: "source" },
  { label: "Người xử lý", key: "handler" },
];

const fmtDate = (d) => (d ? moment(d).tz(TZ).format("DD/MM/YYYY") : "-");
const fmtTime = (d) => (d ? moment(d).tz(TZ).format("HH:mm") : "--:--");
const toYmd = (d) => (d ? moment(d).format("YYYY-MM-DD") : "");
// endDate null = vô thời hạn
const fmtRange = (item) =>
  item?.endDate
    ? `${fmtDate(item.startDate)} - ${fmtDate(item.endDate)}`
    : `Từ ${fmtDate(item?.startDate)} (vô thời hạn)`;

const validityText = (item) => {
  if (item.status !== "APPROVED") return "-";
  const today = moment().tz(TZ).startOf("day");
  if (moment(item.startDate).tz(TZ).isAfter(today)) return "Chưa đến ngày";
  if (item.endDate && moment(item.endDate).tz(TZ).isBefore(today)) return "Hết hiệu lực";
  return "Đang hiệu lực";
};

const StatusCell = ({ status, onApprove, onReject }) => {
  if (status === "APPROVED") {
    return (
      <Flex w={"max-content"} alignItems={"center"} gap={1}>
        <Image src={iconApproved} w={"20px"} />
        <Text color={"var(--color-status-employ)"}>Đã được duyệt</Text>
      </Flex>
    );
  }
  if (status === "REJECTED") {
    return (
      <Flex w={"max-content"} alignItems={"center"} gap={1}>
        <Image src={iconRejected} w={"20px"} />
        <Text color={"var(--text-red-employee)"}>Không được phê duyệt</Text>
      </Flex>
    );
  }
  return (
    <Flex gap={"8px"} w={"max-content"}>
      <Button
        bg={"var(--linear-gradient-employee)"}
        color={"var(--color-main)"}
        fontSize={"12px"}
        lineHeight={"17px"}
        fontWeight={"600"}
        _hover={{ filter: "brightness(90%)" }}
        onClick={onApprove}
        h={"28px"}
        minH={"28px"}
        w={"90px"}
      >
        Duyệt
      </Button>
      <Button
        bg={"var(--color-main)"}
        color={"var(--color-option-employee-hover)"}
        border={"1px solid var(--color-option-employee-hover)"}
        fontSize={"12px"}
        lineHeight={"17px"}
        fontWeight={"600"}
        _hover={{ filter: "brightness(90%)" }}
        onClick={onReject}
        h={"28px"}
        minH={"28px"}
        w={"90px"}
      >
        Ko duyệt
      </Button>
    </Flex>
  );
};

/** Bước 1 - quản lý duyệt trên app (đơn nhập Excel / HCNS thêm không qua bước này) */
const LeaderCell = ({ item }) => {
  if (item.source && item.source !== "APP") return "-";
  if (item.leaderStatus === "APPROVED" || item.leaderStatus === "REJECTED") {
    const approved = item.leaderStatus === "APPROVED";
    return (
      <Flex flexDirection={"column"} gap={1}>
        <Flex w={"max-content"} alignItems={"center"} gap={1}>
          <Image src={approved ? iconApproved : iconRejected} w={"20px"} />
          <Text color={approved ? "var(--color-status-employ)" : "var(--text-red-employee)"}>
            {approved ? "Đã được duyệt" : "Không được phê duyệt"}
          </Text>
        </Flex>
        {item.leaderHandler?.fullName ? (
          <Text>
            {item.leaderHandler.fullName} ({fmtDate(item.leaderHandledAt)}{" "}
            {fmtTime(item.leaderHandledAt)})
          </Text>
        ) : null}
        {!approved && item.leaderRejectReason ? (
          <Text whiteSpace={"normal"}>Lý do: {item.leaderRejectReason}</Text>
        ) : null}
      </Flex>
    );
  }
  if (item.needLeaderStep === false) return "-";
  return <Text color={"var(--text-color-Subtittle)"}>Chưa duyệt</Text>;
};

export default function LateCheckinRequests({ onParamsChange, addOpen, onAddClose }) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(null);
  const [leaderStatus, setLeaderStatus] = useState(null);
  const [validity, setValidity] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [currentPage, setCurrentPage] = useState(1);
  const [handling, setHandling] = useState(null); // { item, status }
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const debouncedSearch = useDebounce(search, 500);

  const onChangeSearch = useCallback((e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  }, []);

  const filters = useMemo(
    () => ({
      search: debouncedSearch,
      status: status || "",
      leaderStatus: leaderStatus || "",
      validity: validity || "",
      fromDate: toYmd(startDate),
      toDate: toYmd(endDate),
    }),
    [debouncedSearch, status, leaderStatus, validity, startDate, endDate]
  );

  // Nút "Xuất DS nhân viên được đi muộn" ở tiêu đề trang xuất theo bộ lọc này
  useEffect(() => {
    onParamsChange?.(filters);
  }, [filters, onParamsChange]);

  const { data, isLoading, isRefetching, refetch } = useQuery({
    queryKey: ["lateCheckinRequest", currentPage, filters],
    queryFn: () => lateCheckinCall.list({ ...filters, page: currentPage, limit: PAGE_SIZE }),
    refetchOnWindowFocus: false,
  });

  const items = useMemo(() => data?.items || [], [data]);
  const entries = data?.pagination?.total || 0;
  const totalPages = Math.ceil(entries / PAGE_SIZE);
  const startEntry = entries ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const endEntry = Math.min(currentPage * PAGE_SIZE, entries);

  const closeHandle = () => {
    setHandling(null);
    setRejectReason("");
  };

  const submitHandle = async () => {
    if (!handling) return;
    setSubmitting(true);
    try {
      await lateCheckinCall.handle(handling.item._id, {
        status: handling.status,
        ...(handling.status === "REJECTED" && rejectReason.trim()
          ? { rejectReason: rejectReason.trim() }
          : {}),
      });
      toast.success(handling.status === "APPROVED" ? "Đã duyệt đơn" : "Đã từ chối đơn");
      closeHandle();
      refetch();
    } catch (error) {
      // lỗi đã báo ở api
    }
    setSubmitting(false);
  };

  const submitForm = async (body) => {
    try {
      if (editItem) {
        await lateCheckinCall.update(editItem._id, body);
        toast.success("Đã cập nhật");
        setEditItem(null);
      } else {
        await lateCheckinCall.create(body);
        toast.success("Đã thêm nhân viên được đi muộn");
        onAddClose?.();
      }
      refetch();
    } catch (error) {
      // lỗi đã báo ở api, giữ popup mở
    }
  };

  const submitDelete = async () => {
    if (!deleteItem) return;
    try {
      await lateCheckinCall.remove(deleteItem._id);
      toast.success("Đã xoá");
      setDeleteItem(null);
      refetch();
    } catch (error) {
      // lỗi đã báo ở api
    }
  };

  const cellValue = (item, key) => {
    switch (key) {
      case "action":
        return (
          <Menu>
            <MenuButton
              _hover={{ backgroundColor: "none" }}
              _active={{ backgroundColor: "none" }}
              background="none"
              as={Button}
              size={"sm"}
            >
              <AiOutlineMore fontSize={"20px"} />
            </MenuButton>
            <MenuList
              fontSize={{ base: "14px" }}
              fontWeight={500}
              lineHeight={"140%"}
              color="var(--color-option-employee)"
            >
              {item.status !== "REJECTED" ? (
                <MenuItem gap={"16px"} onClick={() => setEditItem(item)}>
                  <Image src={iconEdit} /> Chỉnh sửa
                </MenuItem>
              ) : null}
              <MenuItem gap={"16px"} onClick={() => setDeleteItem(item)}>
                <Image src={iconDelete} /> Xóa
              </MenuItem>
            </MenuList>
          </Menu>
        );
      case "status":
        return (
          <StatusCell
            status={item.status}
            onApprove={() => setHandling({ item, status: "APPROVED" })}
            onReject={() => setHandling({ item, status: "REJECTED" })}
          />
        );
      case "leader":
        return <LeaderCell item={item} />;
      case "code":
        return item.employeeObject?.code || "-";
      case "fullName":
        return item.employeeObject?.fullName || "-";
      case "extraTime":
        return `${item.extraTime || 0} phút`;
      case "range":
        return fmtRange(item);
      case "validity":
        return validityText(item);
      case "reason": {
        const parts = [item.reason, item.note].filter(Boolean).join(" | ");
        return item.rejectReason
          ? `${parts || "-"} (Từ chối: ${item.rejectReason})`
          : parts || "-";
      }
      case "images":
        return item.images?.length ? (
          <Flex gap="8px" flexWrap="wrap" maxW="140px">
            {item.images.map((img, i) => (
              <Link key={img} href={`${baseURL}/${img}`} isExternal color="blue.500">
                Ảnh {i + 1}
              </Link>
            ))}
          </Flex>
        ) : (
          "-"
        );
      case "source":
        return SOURCE_TEXT[item.source] || "-";
      case "handler":
        return item.handler?.fullName
          ? `${item.handler.fullName} (${fmtDate(item.handledAt)} ${fmtTime(item.handledAt)})`
          : "-";
      default:
        return "";
    }
  };

  return (
    <Flex flexDirection={"column"} gap={{ base: "16px" }}>
      <Flex
        minHeight="calc(100vh - 167px)"
        backgroundColor="var(--color-main)"
        borderRadius={{ base: "12px" }}
        flexDirection={"column"}
      >
        <Flex
          alignItems={"flex-start"}
          gap={{ base: "16px 32px" }}
          flexDirection={"column"}
          p={"16px 0px 12px 16px"}
        >
          <InputGroup maxW="42vw" minW={"42vw"} borderRadius={{ base: "8px" }} w={{ base: "100%" }}>
            <Input
              h={"44px"}
              placeholder="Nhập MVN, họ tên, lý do..."
              type="text"
              borderRadius={{ base: "8px" }}
              border={{ base: "1px solid var(--color-secondary)" }}
              onChange={onChangeSearch}
            />
            <InputRightElement
              h={"44px"}
              borderTopRightRadius={"8px"}
              borderBottomRightRadius={"8px"}
              pointerEvents="none"
              backgroundImage="var(--color-button)"
            >
              <SearchIcon color="var(--color-main)" />
            </InputRightElement>
          </InputGroup>

          <Flex alignItems={"center"} gap={{ base: "16px 32px" }} flexWrap={"wrap"} w={"100%"}>
            <InputGroup maxW={"275px"} zIndex={22}>
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                dateFormat="dd/MM/yyyy"
                locale="vi"
                onChange={(update) => {
                  setDateRange(update);
                  setCurrentPage(1);
                }}
                isClearable={true}
                placeholderText="Hiệu lực từ ngày - đến ngày"
                className="datePickerStyle"
              />
              {!startDate && !endDate && (
                <InputRightElement pointerEvents="none" h={"44px"}>
                  <Image src={calendarIcon} />
                </InputRightElement>
              )}
            </InputGroup>
            <SearchableSelect
              options={STATUS_OPTIONS}
              initialSelectedOption={{ key: 0, value: "HCNS duyệt: tất cả" }}
              setValue={(v) => {
                setStatus(v || null);
                setCurrentPage(1);
              }}
            />
            <SearchableSelect
              options={LEADER_STATUS_OPTIONS}
              initialSelectedOption={{ key: 0, value: "Quản lý duyệt: tất cả" }}
              setValue={(v) => {
                setLeaderStatus(v || null);
                setCurrentPage(1);
              }}
            />
            <SearchableSelect
              options={VALIDITY_OPTIONS}
              initialSelectedOption={{ key: 0, value: "Hiệu lực: tất cả" }}
              setValue={(v) => {
                setValidity(v || null);
                setCurrentPage(1);
              }}
            />
          </Flex>
        </Flex>

        <Flex
          flexDirection={"column"}
          justifyContent={"space-between"}
          width={"100%"}
          minH="calc(100vh - 235px)"
        >
          <Skeleton isLoaded={!isLoading && !isRefetching}>
            <TableContainer w={"100%"} pb={"32px"} maxH="calc(100vh - 350.5px)" overflowY="auto">
              <Table w={"100%"} variant="unstyled">
                <Thead position="sticky" top="0" zIndex="1" w={"100%"} h={"41px"} backgroundColor="#F9FAFD">
                  <Tr h={{ base: "41px" }}>
                    {HEADERS.map((h, index) => (
                      <Td
                        p={"16px 16px 8px 16px"}
                        key={index}
                        border={"none"}
                        color={"#51607B"}
                        fontSize={{ base: "14px" }}
                      >
                        <Text fontWeight={"bold"} textAlign={"start"}>
                          {h.label}
                        </Text>
                      </Td>
                    ))}
                  </Tr>
                </Thead>
                <Tbody w={"100%"} h={"100%"}>
                  {items.length > 0 ? (
                    items.map((item, rowIndex) => {
                      const bg = rowIndex % 2 === 0 ? "var(--color-main)" : "var(--fill-avatar)";
                      return (
                        <Tr key={item._id} h={"41px"}>
                          {HEADERS.map((h) => (
                            <Td
                              key={h.key}
                              p={h.key === "action" ? "8px" : "16px"}
                              bg={bg}
                              // Lý do (kèm ghi chú file import) dài => cột rộng cố định, chữ xuống dòng
                              minW={h.key === "reason" ? "360px" : undefined}
                              maxW={h.key === "reason" ? "480px" : h.key === "leader" ? "320px" : undefined}
                            >
                              <Box
                                fontSize={{ base: "14px" }}
                                lineHeight={"19.6px"}
                                textAlign={"start"}
                                fontWeight={500}
                                whiteSpace={h.key === "reason" ? "normal" : "nowrap"}
                                color="var(--color-option-employee)"
                              >
                                {cellValue(item, h.key)}
                              </Box>
                            </Td>
                          ))}
                        </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={HEADERS.length} textAlign="center" padding={"40px 0"}>
                        Không có dữ liệu
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Skeleton>

          <Flex m={"50px 16px 16px 16px"} alignItems={"center"} justifyContent={"space-between"}>
            <Text fontSize={"14px"} fontWeight={500} lineHeight={"20px"} color={"var(--text-color-Subtittle)"}>
              Showing {startEntry} to {endEntry} of {entries} entries
            </Text>
            <HStack spacing={2} justify="flex-end">
              <ReactPaginate
                className="pagination-custom"
                pageCount={totalPages}
                pageRangeDisplayed={3}
                marginPagesDisplayed={1}
                onPageChange={(p) => setCurrentPage(p.selected + 1)}
                containerClassName={"pagination"}
                activeClassName={"active"}
                breakClassName={"ellipsis"}
                breakLabel={"..."}
                previousLabel={<Img w={"16px"} minW={"16px"} h={"16px"} src={chevronLeft} />}
                nextLabel={<Img w={"16px"} minW={"16px"} h={"16px"} src={chevronRight} />}
                renderOnZeroPageCount={null}
                forcePage={currentPage - 1}
              />
            </HStack>
          </Flex>
        </Flex>
      </Flex>

      <Modal isOpen={!!handling} onClose={closeHandle} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius={"16px"}>
          <ModalHeader>
            {handling?.status === "APPROVED" ? "Duyệt đơn xin đi muộn" : "Từ chối đơn xin đi muộn"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb="8px" fontSize="14px">
              {handling?.item?.employeeObject?.fullName}: được đi muộn {handling?.item?.extraTime} phút,{" "}
              {fmtRange(handling?.item)}
            </Text>
            {handling?.item?.source === "APP" && handling.item.leaderStatus !== "APPROVED" ? (
              <Text mb="8px" fontSize="14px" color="var(--text-red-employee)">
                {handling.item.leaderStatus === "REJECTED"
                  ? "Quản lý đã từ chối đơn này ở bước 1."
                  : "Quản lý chưa duyệt đơn này ở bước 1."}
              </Text>
            ) : null}
            {handling?.status === "APPROVED" ? (
              <Text fontSize="14px" color="var(--text-color-Subtittle)">
                Duyệt xong, các lần chấm công trong thời gian hiệu lực được cộng số phút trên vào giờ
                đúng giờ (không tính lại những ngày đã chấm trước khi duyệt).
              </Text>
            ) : (
              <Textarea
                placeholder="Lý do từ chối (không bắt buộc)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            )}
          </ModalBody>
          <ModalFooter gap="12px">
            <Button h="44px" variant="outline" border="1px solid var(--color-secondary)" onClick={closeHandle}>
              Huỷ
            </Button>
            <Button
              h="44px"
              color="var(--color-main)"
              background={
                handling?.status === "APPROVED"
                  ? "var(--linear-gradient-employee)"
                  : "var(--text-red-employee)"
              }
              _hover={{ filter: "brightness(90%)" }}
              isLoading={submitting}
              onClick={submitHandle}
            >
              {handling?.status === "APPROVED" ? "Duyệt" : "Từ chối"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <LateCheckinForm
        isOpen={!!addOpen || !!editItem}
        editItem={editItem}
        onClose={() => {
          setEditItem(null);
          onAddClose?.();
        }}
        onSubmit={submitForm}
      />

      <DeleteDialog
        isOpen={!!deleteItem}
        onClose={() => setDeleteItem(null)}
        title={"Xóa đơn xin đi muộn"}
        description={
          deleteItem
            ? `Bạn có chắc chắn muốn xóa đơn xin đi muộn của nhân viên ${deleteItem.employeeObject?.code || ""} - ${
                deleteItem.employeeObject?.fullName || ""
              } (${fmtRange(deleteItem)}) không? Nhân viên sẽ không còn được đi muộn theo đơn này.`
            : ""
        }
        action={submitDelete}
      />
    </Flex>
  );
}
